import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-time-off-balance/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'

// helpers
import Axios from '../../helpers/axios'
import useToastMessage from 'src/helpers/toast-message'

const EditTimeOffBalance = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();
  const params = useParams()
  const { id } = params

  const isLoading = useSelector((state) => state.isLoading)

  const [timeOffBalanceDetail, setTimeOffBalanceDetail] = useState('')
  const [employeeOptions, setEmployeeOptions] = useState([])

  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const formik = useFormik({
    initialValues: {
      employee_id: timeOffBalanceDetail?.employee?.id || '',
      adjustment_type: "accrual",
      days_amount: timeOffBalanceDetail?.summary?.used_balance || '',
      adjustment_reason: "",
    },
    validationSchema: Yup.object().shape({
      employee_id: Yup.string().required("Please choose employee."),
      adjustment_type: Yup.string().required("Please enter adjustment type."),
      days_amount: Yup.number().required("Please enter adjustment balance.").positive("Adjustment balance must be a positive number"),
      adjustment_reason: Yup.string().required("Please enter adjustment reason."),
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchDetailTimeOffBalance()
    fetchEmployeeOptions()
  }, [id])

  const fetchDetailTimeOffBalance = () => {
    Axios({
      method: "get",
      url: `/api/tracker/v1/leave/balance/employee/${id}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          setTimeOffBalanceDetail(data?.value)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchEmployeeOptions = () => {
    Axios({
      method: "get",
      url: `/api/tracker/v1/employee/option/list`,
    })
      .then(({ data }) => {
        if (data?.success) {
          const employeeOptions = data?.value.map((item) => ({
            value: item?.id,
            label: item?.name,
          }));
          setEmployeeOptions(employeeOptions);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleInputValue = (values) => {
    const payload = {
      employee_id: values?.employee_id,
      leave_type_id: 1,
      year: new Date().getFullYear(),
      days_amount: values?.days_amount,
      adjustment_reason: values?.adjustment_reason,
    }

    return payload
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    let url = `/api/tracker/v1/leave/balance`

    if (formik?.values?.adjustment_type === 'accrual') {
      url += `/add`
    } else if (formik?.values?.adjustment_type === 'deduction') {
      url += `/reduce`
    }

    Axios({
      method: "post",
      url,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('Time-off balance has been successfully updated')
          navigate(`/time-off-balance/details/${id}`)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSelectType = (e) => {
    formik.setValues((values) => ({
      ...values,
      adjustment_type: e?.target?.name === 'adjustment_type_accrual' ? 'accrual' : 'deduction'
    }), false)
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  return (
    <>
      {
        showModalConfirm.show && (
          <AppModalConfirmation
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want to edit this employee's time-off balance?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard
        className='mb-4'
        headerTitle={<h4>Edit Time-Off Balance</h4>}
        bodyContent={
          <AppForm
            form="edit"
            values={formik.values}
            handleChange={formik.handleChange}
            handleSelectType={handleSelectType}
            employeeOptions={employeeOptions}
            errors={formik.errors}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate(`/time-off-balance/details/${id}`)}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default EditTimeOffBalance
